import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import ledger from "../../api/ledger";
import PipelineBoard, {
  BOARD_STATUS_QUERY,
  TERMINAL_BOARD_STATUSES,
} from "./component/PipelineBoard";

/* The live pipeline board (columns, drag rules, prompts) lives in
   ./component/PipelineBoard — it also owns the list of statuses the board
   needs, so this poller always asks for exactly what the columns show.
   The board tells us when a drag or a modal is in flight; while that is
   true the 10s board refresh is skipped so cards never jump under the
   cursor. Stat cards and low stock keep ticking regardless. */
const BOARD_FETCH_LIMIT = 150;

/* ─── board rows: never let a busy day starve the active columns ───────
   Delivered and Completed now only render TODAY (see PipelineBoard), but
   the API still hands back every terminal order inside the limit window.
   On a heavy day those can crowd out the New → Out-for-delivery rows the
   owner actually has to act on, and an active card dropped from the board
   is an order nobody sees.

   So: one request, unchanged — then partition client-side and put every
   ACTIVE row (New … Out for delivery + Needs attention) ahead of every
   terminal one. Nothing is discarded here; the ordering just guarantees
   that any cap downstream bites the delivered/completed tail first.
   Terminal rows are sorted newest-first so the ones that survive are the
   ones today's buckets need.

   PROPER FIX IS SERVER-SIDE: GET /admin/orders should accept a date window
   for terminal statuses (e.g. &updatedFrom=<IST midnight>) or return the
   board's active and terminal slices with independent limits. Until then
   the "+N earlier" counter is a count within the fetched window, not the
   whole archive.                                                        */
const TERMINAL_STATUS_SET = new Set(TERMINAL_BOARD_STATUSES);

const rowStamp = (o) => {
  const t = new Date(o?.updatedAt || o?.placedAt || 0).getTime();
  return Number.isFinite(t) ? t : 0;
};

const prioritiseBoardRows = (rows) => {
  const active   = [];
  const terminal = [];
  (Array.isArray(rows) ? rows : []).forEach((o) => {
    /* Rows carry no _id any more — orderNo is what addresses an order. */
    if (!o || !o.orderNo) return;
    if (TERMINAL_STATUS_SET.has(o.status)) terminal.push(o);
    else active.push(o);          // server order preserved for active cards
  });
  terminal.sort((a, b) => rowStamp(b) - rowStamp(a));
  return active.concat(terminal);
};

/* ═══════════════════════════════════════════════════════════════
   LOW STOCK STRIP

   PRIMARY SOURCE: GET /admin/low-stock — thresholded, worst-first and
   store-scoped from the token, answering
   {threshold, thresholdGrams, channel, storeId, items, limit, offset}.
   Its rows are per ITEM (sku), so the strip names the actual pack. The
   default channel is `pack`, so `available` is a pack count.

   FALLBACK: /admin/catalog/products reports ONE number per product —
   packsOnHand, packs on hand minus reserved for the acting store, pack
   items only (bulk grams are a separate pool and are deliberately not
   added to it). It is filtered and sorted client-side, and it is what the
   strip falls back to when the primary read is refused (a staff token gets
   403 there).
═══════════════════════════════════════════════════════════════ */
const LOW_STOCK_DEFAULT   = 10;
const LOW_STOCK_SHOWN     = 5;
const FALLBACK_EVERY_TICK = 6;    // 6 × 10s = refresh the fallback once a minute

/* ═══════════════════════════════════════════════════════════════
   OPEN COUNTER BILLS

   A bill held at the till is a draft on the server, so it survives the
   cashier walking away from the screen -- but until now it could only be
   seen from inside the till, which is where the owner is not. A draft
   that has been open for an hour is not a held bill any more: it is a
   customer who left, or a till nobody closed.

   So the card leads with age rather than with the total, and anything
   past HELD_STALE_MIN is called out. Clicking one opens the till with
   that bill already recalled.                                          */
const HELD_STALE_MIN = 45;

const minutesSince = (iso) => {
  const t = new Date(iso || 0).getTime();
  if (!Number.isFinite(t) || t <= 0) return null;
  return Math.max(0, Math.round((Date.now() - t) / 60000));
};

const ageWords = (mins) => {
  if (mins === null) return "";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h} hr` : `${Math.floor(h / 24)} d`;
};

const numOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/* normalise a /admin/low-stock item into a strip entry. `available` is the
   figure the server thresholded on — on hand minus reserved — and `label` is
   the pack it belongs to, so this row CAN name the worst pack. */
const normalizeLowStockItem = (row) => {
  const total = numOrNull(row?.available) ?? 0;
  return {
    id:   row?.sku || row?.productId,
    name: row?.name || "—",
    pack:  row?.label || row?.sku || "base stock",
    count: total,
    total,
  };
};

/* normalise a catalogue row into a strip entry (fallback source) */
const normalizeLowRow = (row) => {
  const total = numOrNull(row?.packsOnHand) ?? 0;
  return {
    id:   row?.id,
    name: row?.name || "—",
    /* the catalogue row carries one number for the whole product, so the
       worst PACK cannot be named from it — /admin/low-stock can. */
    pack:  "base stock",
    count: total,
    total,
  };
};

const Dashboard = () => {
  /* Refresh-button feedback only — every fetch is best-effort */
  const [refreshing, setRefreshing] = useState(false);

  /* One server call owns every number on the strip: today on the STORE's
     clock, delivery AND pickup together, revenue excluding cancelled. The
     old cards derived counts from delivery-only status buckets and showed
     0 while the pipeline board plainly held orders. */
  const [summary, setSummary] = useState({
    orders: 0, revenuePaise: 0, taxPaise: 0,
    done: 0, cancelled: 0, inProgress: 0,
    delivery: 0, pickup: 0,
  });

  /* No params = today on the STORE's clock. Counts keep their names; the
     money is renamed and is integer PAISE (revenuePaise/taxPaise). */
  const fetchSummary = async () => {
    try {
      const res = await api.get("/admin/orders/summary");
      if (res.data && typeof res.data.orders === "number") setSummary(res.data);
    } catch (e) { /* strip is best-effort; the board still renders */ }
  };

  const navigate = useNavigate();
  const [boardOrders, setBoardOrders] = useState([]);
  /* true while the board has a drag in progress or a modal open */
  const boardBusyRef = useRef(false);
  const handleBoardBusy = useCallback((busy) => { boardBusyRef.current = !!busy; }, []);

  /* ── low stock strip ── */
  const [openBills,     setOpenBills]     = useState([]);
  const [lowStock,      setLowStock]      = useState([]);
  const [lowThreshold,  setLowThreshold]  = useState(LOW_STOCK_DEFAULT);
  const lowFallbackRef = useRef(false);   // true once /admin/low-stock is unusable
  const tickRef        = useRef(0);
  /* the 10s poller closes over the mount render — read the threshold
     through a ref so a later store-settings load is picked up */
  const thresholdRef   = useRef(LOW_STOCK_DEFAULT);

  /* ── ONE ROUND AT A TIME ──
     The board asks for 150 orders every ten seconds. On a busy day that query
     can take longer than ten seconds, and the old timer did not care: it fired
     again anyway, and again, until the requests were queueing on a server the
     till shares. So each fetch marks itself in flight and a tick that arrives
     while the last one is still out is simply skipped. A dashboard one tick
     behind is invisible; a dashboard that saturates the API is not. */
  const boardInFlight = useRef(false);
  const fetchBoard = async () => {
    if (boardInFlight.current) return;
    boardInFlight.current = true;
    try {
      /* One of the three routes that still wraps its payload:
         {data:[...], pagination:{...}}. */
      const res = await api.get(
        `/admin/orders?status=${BOARD_STATUS_QUERY}&limit=${BOARD_FETCH_LIMIT}`
      );
      setBoardOrders(prioritiseBoardRows(res.data?.data || []));
    } catch (e) { /* board is best-effort; stats still render */ }
    finally { boardInFlight.current = false; }
  };

  /* Every open draft, newest first, across every store: the ledger does not
     narrow an admin to their own store yet, so neither does this. A till that
     cannot be reached is not worth a red box on the dashboard -- the card
     simply says nothing, and the number it would have shown is not invented. */
  const fetchOpenBills = async () => {
    try {
      const { data } = await ledger.get("/billing/bills", { params: { state: "draft", limit: 20 } });
      const rows = Array.isArray(data) ? data : data?.data || [];
      setOpenBills(rows);
    } catch {
      setOpenBills([]);
    }
  };

  /* Throwing an abandoned bill away, from here rather than from the till. The
     row disappears optimistically so the list does not sit there looking
     unchanged for up to ten seconds, and the next poll is the real answer. */
  const discardBill = async (b) => {
    const label = b.partyName || b.docNo || `#${b.id}`;
    if (!window.confirm(`Discard the open bill ${label}? The items on it are not saved.`)) return;
    setOpenBills((rows) => rows.filter((r) => r.id !== b.id));
    try {
      await ledger.post(`/billing/bills/${b.id}/cancel`, { reason: "discarded from the dashboard" });
    } catch {
      /* it is still open; the next poll puts it back where it was */
    }
    fetchOpenBills();
  };

  /* derive the low list from catalogue rows — one packsOnHand per product,
     filtered and sorted client-side because no route does either */
  const fetchLowStockFallback = async () => {
    try {
      const res  = await api.get(`/admin/catalog/products?page=1&limit=100`);
      const rows = res.data?.data || [];
      const list = (Array.isArray(rows) ? rows : [])
        .map(normalizeLowRow)
        .filter((r) => r.total !== null && r.total <= thresholdRef.current)
        .sort((a, b) => a.total - b.total)
        .slice(0, LOW_STOCK_SHOWN);
      setLowStock(list);
    } catch (e) { /* leave the card showing whatever it last had */ }
  };

  const fetchLowStock = async () => {
    if (lowFallbackRef.current) return fetchLowStockFallback();
    try {
      /* The body IS the payload: {threshold, …, items:[…]}. The server has
         already thresholded, sorted worst-first and limited, so nothing is
         re-done here. Its store scope comes from the token, never a param. */
      const res  = await api.get(
        `/admin/low-stock?threshold=${thresholdRef.current}&limit=${LOW_STOCK_SHOWN}`
      );
      const rows = res.data?.items || [];
      setLowStock((Array.isArray(rows) ? rows : []).map(normalizeLowStockItem));
    } catch (e) {
      /* 403 for a staff token, or the route is unreachable — latch the
         client-side fallback rather than emptying the card every tick */
      lowFallbackRef.current = true;
      await fetchLowStockFallback();
    }
  };

  const fetchAll = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchSummary(), fetchBoard(), fetchLowStock(), fetchOpenBills()]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    /* The body is the settings object itself, not {data:{...}}.
       TODO(cutover): server does not return lowStockThreshold — it has no
       column on the .NET service and is refused on write, so the key is
       simply absent and the LOW_STOCK_DEFAULT of 10 stands. */
    api.get("/admin/store-settings")
      .then((res) => {
        const t = Number(res.data?.lowStockThreshold);
        if (Number.isFinite(t) && t >= 0) {
          thresholdRef.current = t;
          setLowThreshold(t);
          /* the primary read takes ?threshold=, so re-run whichever source
             is in play rather than only the client-side one */
          fetchLowStock();
        }
      })
      .catch(() => { /* threshold unreachable — default of 10 stands */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
    // the board keeps itself fresh — no manual refresh needed.
    // Low stock rides the same 10s poller; the client-side fallback is
    // heavier, so it only re-runs once a minute.
    const t = setInterval(() => {
      /* A dashboard on a back tab is a dashboard nobody is reading. It used to
         keep asking for 150 orders every ten seconds all the same, from every
         tab anyone had left open. */
      if (document.visibilityState === "hidden") return;
      tickRef.current += 1;
      /* never re-shuffle the board mid-drag or under an open modal */
      if (!boardBusyRef.current) fetchBoard();
      fetchSummary();
      fetchOpenBills();
      if (!lowFallbackRef.current || tickRef.current % FALLBACK_EVERY_TICK === 0) {
        fetchLowStock();
      }
    }, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── stat strip ──
     Five numbers that answer "how is today going?" at a glance, all from
     /admin/orders/summary (store-timezone day, delivery + pickup).
     Revenue is real money: cancelled orders excluded, GST shown inside.
     The server sends integer PAISE — without the /100 the cards read 100x
     high and nothing on the page says so. */
  const inr = (paise) =>
    "₹" + ((Number(paise) || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  const cards = [
    {
      key: "orders",
      label: "Orders today",
      value: summary.orders,
      sub: `${summary.delivery} delivery · ${summary.pickup} pickup`,
      accent: "var(--mly-heading)",
      chip: "🛍️",
    },
    {
      key: "revenue",
      label: "Revenue today",
      value: inr(summary.revenuePaise),
      sub: summary.taxPaise > 0 ? `incl. GST ${inr(summary.taxPaise)}` : "excl. cancelled",
      accent: "var(--mly-accent-2)",
      chip: "💰",
    },
    {
      key: "progress",
      label: "In progress",
      value: summary.inProgress,
      sub: "accepted → out for delivery",
      accent: "var(--mly-accent)",
      chip: "⏳",
    },
    {
      key: "done",
      label: "Delivered / done",
      value: summary.done,
      sub: "incl. pickups handed over",
      accent: "var(--mly-heading)",
      chip: "✅",
    },
    {
      key: "cancelled",
      label: "Cancelled",
      value: summary.cancelled,
      sub: summary.cancelled > 0 ? "needs a look" : "all good",
      accent: summary.cancelled > 0 ? "#C0392B" : "var(--mly-text-muted)",
      chip: "🚫",
    },
  ];

  return (
    <div>
      <div className="container-fluid padding" />

      <div className="container-fluid crypto-dash">
        <div className="row">
          <div className="container-fluid">
            <div className="row">

              {/* ── Header ── */}
              <div className="col-12">
                {/* ── brand eyebrow ── */}
                <div style={{
                  fontSize: 10.5, fontWeight: 700, color: "var(--mly-accent-2)",
                  textTransform: "uppercase", letterSpacing: "1.5px",
                  marginBottom: 4,
                }}>
                  Into Today&#39;s Kitchen &mdash; For Tomorrow&#39;s Generation
                </div>
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <h4 className="mb-0 d-flex gap-2">
                    <span style={{ background: "#fff3ed", padding: 6, borderRadius: 8 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7.6 1.9a1.1 1.1 0 0 1 1.1 1.1v1h6.6V3a1.1 1.1 0 0 1 2.2 0v1h1.3A2.6 2.6 0 0 1 21.4 6.6v12.4a2.6 2.6 0 0 1-2.6 2.6H5.2a2.6 2.6 0 0 1-2.6-2.6V6.6A2.6 2.6 0 0 1 5.2 4h1.3V3a1.1 1.1 0 0 1 1.1-1.1ZM4.8 9.4v9.6a.4.4 0 0 0 .4.4h13.6a.4.4 0 0 0 .4-.4V9.4Z" /></svg>
                    </span>
                    Today Orders
                  </h4>
                  <div className="d-flex gap-2 align-items-center">
                    <button
                      className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                      onClick={fetchAll}
                      disabled={refreshing}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3.4a8.6 8.6 0 0 0-6.9 3.5h2.6a1.1 1.1 0 0 1 0 2.2H2.9a1.1 1.1 0 0 1-1.1-1.1V3.2a1.1 1.1 0 0 1 2.2 0v1.9A10.8 10.8 0 0 1 22.6 10a1.1 1.1 0 0 1-2.2.3A8.6 8.6 0 0 0 12 3.4Zm-9.5 9.4a1.1 1.1 0 0 1 1.3.9A8.6 8.6 0 0 0 18.9 17h-2.6a1.1 1.1 0 0 1 0-2.2h4.8a1.1 1.1 0 0 1 1.1 1.1v4.8a1.1 1.1 0 0 1-2.2 0v-1.9A10.8 10.8 0 0 1 1.6 14.1a1.1 1.1 0 0 1 .9-1.3Z" /></svg>
                      {refreshing ? "Loading…" : "Refresh"}
                    </button>
                    <span
                      className="badge"
                      style={{ background: "#ffecec", color: "#c0392b", borderRadius: 20, padding: "5px 12px" }}
                    >
                      • Live
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Stat strip — five real numbers, one row, no filler ── */}
              <div className="col-12">
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 12,
                  marginBottom: 4,
                }}>
                  {cards.map((card) => (
                    <div key={card.key} style={{
                      background: "var(--mly-surface)",
                      border: "1px solid var(--mly-border)",
                      borderLeft: `4px solid ${card.accent}`,
                      borderRadius: 14,
                      padding: "14px 16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      minWidth: 0,
                    }}>
                      <div style={{
                        display: "flex", justifyContent: "space-between",
                        alignItems: "center", gap: 8,
                      }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, color: "var(--mly-text-soft)",
                          textTransform: "uppercase", letterSpacing: 0.7,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}>
                          {card.label}
                        </span>
                        <span style={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }} aria-hidden>
                          {card.chip}
                        </span>
                      </div>
                      <span style={{
                        fontSize: 26, fontWeight: 800, color: card.accent,
                        lineHeight: 1.15, fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {card.value}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--mly-text-muted)" }}>{card.sub}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Open counter bills — drafts held at the till ── */}
              <div className="col-xl-4 col-md-6" style={{ marginTop: 8 }}>
                <div style={{
                  background: "var(--mly-surface)", border: "1px solid var(--mly-border)", borderRadius: 14,
                  padding: 12, height: "100%",
                }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    marginBottom: 8, paddingBottom: 6, borderBottom: "2px solid var(--mly-accent)",
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--mly-heading)" }}>
                      Open bills at the counter
                      {openBills.length > 0 && (
                        <span style={{ fontWeight: 600, color: "var(--mly-text-muted)", marginLeft: 6, fontSize: 10.5 }}>
                          {openBills.length}
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate("/admin/pos")}
                      style={{
                        background: "var(--mly-accent)", color: "var(--mly-on-accent, #FDF6E3)", border: "none",
                        borderRadius: 999, fontSize: 10.5, fontWeight: 700,
                        padding: "3px 10px", cursor: "pointer", whiteSpace: "nowrap",
                      }}
                    >
                      Open till
                    </button>
                  </div>

                  {openBills.length === 0 ? (
                    <div style={{ fontSize: 11.5, color: "var(--mly-text-muted)", textAlign: "center", padding: "16px 0" }}>
                      Nothing left open at the till
                    </div>
                  ) : (
                    openBills.slice(0, 5).map((b) => {
                      const mins  = minutesSince(b.issuedAt);
                      const stale = mins !== null && mins >= HELD_STALE_MIN;
                      return (
                        <div
                          key={b.id}
                          /* The bill's own store travels with it: the till
                             prices, searches and stocks against whichever
                             store it has adopted, and a draft from the other
                             branch opened under this one would look right and
                             be wrong. */
                          onClick={() => navigate(`/admin/pos?bill=${b.id}${b.storeId ? `&store=${b.storeId}` : ""}`)}
                          style={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            gap: 8, padding: "6px 8px", marginBottom: 4, cursor: "pointer",
                            background: "var(--mly-surface-2)", border: "1px solid var(--mly-border)", borderRadius: 10,
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--mly-accent)")}
                          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--mly-border)")}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{
                              fontSize: 11.5, fontWeight: 700, color: "var(--mly-heading)",
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            }}>
                              {b.partyName || b.docNo || `#${b.id}`}
                            </div>
                            <div style={{ fontSize: 10.5, color: stale ? "#C0392B" : "var(--mly-text-soft)" }}>
                              {b.lineCount || 0} item{(b.lineCount || 0) === 1 ? "" : "s"}
                              {mins !== null && ` · held ${ageWords(mins)}`}
                            </div>
                          </div>
                          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{
                              background: "var(--mly-surface)", color: "var(--mly-accent)",
                              border: "1px solid var(--mly-accent-2)",
                              borderRadius: 999, fontSize: 11, fontWeight: 800,
                              padding: "2px 10px", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
                            }}>
                              {inr(b.totalPaise)}
                            </span>
                            {/* A bill that has been open an hour is usually one
                                nobody is coming back for. Closing it from here
                                saves walking to the till to do it. */}
                            <button
                              type="button"
                              title="Discard this bill"
                              aria-label={`Discard bill ${b.docNo || b.id}`}
                              onClick={(e) => { e.stopPropagation(); discardBill(b); }}
                              style={{
                                border: "none", background: "transparent", cursor: "pointer",
                                color: "var(--mly-text-muted)", fontSize: 12, padding: "2px 4px", lineHeight: 1,
                              }}
                            >
                              ✕
                            </button>
                          </span>
                        </div>
                      );
                    })
                  )}
                  {openBills.length > 5 && (
                    <div style={{ fontSize: 10.5, color: "var(--mly-text-muted)", textAlign: "center", paddingTop: 4 }}>
                      and {openBills.length - 5} more at the till
                    </div>
                  )}
                </div>
              </div>

              {/* ── Low stock — worst 5, rides the same 10s poller ── */}
              <div className="col-xl-4 col-md-6" style={{ marginTop: 8 }}>
                <div
                  style={{
                    background: "var(--mly-surface)", border: "1px solid var(--mly-border)", borderRadius: 14,
                    padding: 12, height: "100%",
                  }}
                >
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    marginBottom: 8, paddingBottom: 6, borderBottom: "2px solid var(--mly-accent-2)",
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "var(--mly-heading)" }}>
                      Low stock
                      <span style={{ fontWeight: 600, color: "var(--mly-text-muted)", marginLeft: 6, fontSize: 10.5 }}>
                        ≤ {lowThreshold}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate("/admin/products/stockentry")}
                      style={{
                        background: "#C9952A", color: "#FDF6E3", border: "none",
                        borderRadius: 999, fontSize: 10.5, fontWeight: 700,
                        padding: "3px 10px", cursor: "pointer", whiteSpace: "nowrap",
                      }}
                    >
                      Manage stock
                    </button>
                  </div>

                  {lowStock.length === 0 ? (
                    <div style={{ fontSize: 11.5, color: "var(--mly-text-muted)", textAlign: "center", padding: "16px 0" }}>
                      Everything above the threshold
                    </div>
                  ) : (
                    lowStock.map((r) => (
                      <div
                        key={r.id || `${r.name}-${r.pack}`}
                        onClick={() => navigate("/admin/products/stockentry")}
                        style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          gap: 8, padding: "6px 8px", marginBottom: 4, cursor: "pointer",
                          background: "var(--mly-surface-2)", border: "1px solid var(--mly-border)", borderRadius: 10,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--mly-accent-2)")}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--mly-border)")}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontSize: 11.5, fontWeight: 700, color: "var(--mly-heading)",
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          }}>
                            {r.name}
                          </div>
                          <div style={{ fontSize: 10.5, color: "var(--mly-text-soft)" }}>{r.pack}</div>
                        </div>
                        <span style={{
                          background: r.count === 0 ? "#fff5f5" : "var(--mly-surface)",
                          color:      r.count === 0 ? "#ef4444" : "var(--mly-accent)",
                          border: `1px solid ${r.count === 0 ? "#fecaca" : "var(--mly-accent-2)"}`,
                          borderRadius: 999, fontSize: 11, fontWeight: 800,
                          padding: "2px 10px", whiteSpace: "nowrap",
                        }}>
                          {r.count}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* ── Live pipeline board — drag & drop, refreshes every 10s ── */}
              <div className="col-12" style={{ marginTop: 8 }}>
                <PipelineBoard
                  orders={boardOrders}
                  onRefresh={fetchBoard}
                  onBusyChange={handleBoardBusy}
                />
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;